import { useState } from 'preact/compat'
import fetchDataFromAPI, {
  InstitutionType,
} from '../../lib/salary-statistics/api'
import SelectWrapper from './select-wrapper'
import SalaryStatisticsOverview from './overview'
import {
  institutionTypeDefault,
  institutionTypes,
  seniorityDefault,
  seniorityOptions,
  weeklyHoursDefault,
  weeklyHoursOptions,
} from '../../config/salary-statistics'

// State holds the raw <select> string values; converted to numbers where the
// calculation needs them.
export type SelectedInputs = {
  seniority: string | undefined
  institutionType: string | undefined
  weeklyHours: string | undefined
}

export type Salaries = {
  fr10: number | null
  fr25: number | null
  fr30: number | null
  fr50: number | null
  fr70: number | null
  fr75: number | null
  fr90: number | null
}

function SalaryStatistics() {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isCalculated, setIsCalculated] = useState<boolean>(false)
  const STANDARD_WEEKLY_HOURS = 37

  // Some state we use to make sure we only calculate when we have all the
  // relevant data.
  const [selectedInputs, setSelectedInputs] = useState<SelectedInputs>({
    seniority: undefined,
    institutionType: undefined,
    weeklyHours: undefined,
  })

  const [salaries, setSalaries] = useState<Salaries>({
    fr10: null,
    fr25: null,
    fr30: null,
    fr50: null,
    fr70: null,
    fr75: null,
    fr90: null,
  })

  const [errorMessage, setErrorMessage] = useState<null | string>(null)

  const handleSelectChange = (value: string, name: keyof SelectedInputs) => {
    // Make sure that we remove the overview when user changes the input.
    setIsCalculated(false)

    setSelectedInputs((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleCalculation = async () => {
    try {
      setIsLoading(true)
      if (
        selectedInputs.institutionType === undefined ||
        selectedInputs.seniority === undefined ||
        selectedInputs.weeklyHours === undefined
      ) {
        setIsCalculated(false)
        setIsLoading(false)
        throw new Error('Missing inputs')
      }

      const hourProportion =
        Number(selectedInputs.weeklyHours) / STANDARD_WEEKLY_HOURS

      const response = await fetchDataFromAPI(
        selectedInputs.institutionType as InstitutionType,
        Number(selectedInputs.seniority),
      )

      if (!response.data.length) {
        setIsCalculated(false)
        setIsLoading(false)
        return
      }

      setSalaries({
        fr10: response.data[0].fr10 * hourProportion,
        fr25: response.data[0].fr25 * hourProportion,
        fr30: response.data[0].fr30 * hourProportion,
        fr50: response.data[0].fr50 * hourProportion,
        fr70: response.data[0].fr70 * hourProportion,
        fr75: response.data[0].fr75 * hourProportion,
        fr90: response.data[0].fr90 * hourProportion,
      })
      setIsCalculated(true)
      setIsLoading(false)
    } catch (error) {
      console.error(error)
      setErrorMessage('Der gik desværre noget galt i beregningen. Prøv igen.')
    }
  }

  return (
    <>
      <div className="salary-statistics">
        <h2>Lønstatistik</h2>
        <h3 className="typo__body">Hvad tjener en pædagog som dig?</h3>
        <div className="bupl-form">
          <div className="seniority">
            <label
              htmlFor="seniority"
              className="typo__bordeaux-color typo__small"
            >
              Anciennitet
            </label>
            <SelectWrapper>
              <select
                className="form-select"
                id="seniority"
                defaultValue={seniorityDefault.value}
                onChange={(e) =>
                  handleSelectChange(e.currentTarget.value, 'seniority')
                }
              >
                {seniorityOptions.map((year) => (
                  <option
                    key={year.value}
                    value={year.value}
                    disabled={year.value === seniorityDefault.value}
                  >
                    {/* If the label is 20, we want to show it as 20+ as people
                    can have more than 20 years of seniority. */}
                    {year.label !== 20 ? year.label : `${year.label}+`}
                  </option>
                ))}
              </select>
              <span>{selectedInputs.seniority ?? seniorityDefault.label}</span>
            </SelectWrapper>
          </div>

          <div className="weekly-hours">
            <label
              htmlFor="weekly-hours"
              className="typo__bordeaux-color typo__small"
            >
              Ugentlige timer
            </label>
            <SelectWrapper>
              <select
                className="form-select"
                id="weekly-hours"
                defaultValue={weeklyHoursDefault.value}
                onChange={(e) =>
                  handleSelectChange(e.currentTarget.value, 'weeklyHours')
                }
              >
                {weeklyHoursOptions.map((hour) => (
                  <option
                    key={hour.value}
                    value={hour.value}
                    disabled={hour.value === weeklyHoursDefault.value}
                  >
                    {hour.label}
                  </option>
                ))}
              </select>
              <span>
                {selectedInputs.weeklyHours ?? weeklyHoursDefault.label}
              </span>
            </SelectWrapper>
          </div>

          <div className="institution-type">
            <label
              htmlFor="institution-type"
              className="typo__bordeaux-color typo__small"
            >
              Område
            </label>
            <SelectWrapper>
              <select
                className="form-select"
                id="institution-type"
                defaultValue={institutionTypeDefault.value}
                onChange={(e) =>
                  handleSelectChange(e.currentTarget.value, 'institutionType')
                }
              >
                {institutionTypes.map((type) => {
                  return (
                    <option
                      key={type.value}
                      value={type.value}
                      disabled={type.value === institutionTypeDefault.value}
                    >
                      {type.label}
                    </option>
                  )
                })}
              </select>
              <span>
                {institutionTypes.find(
                  (type) => type.value === selectedInputs.institutionType,
                )?.label ?? institutionTypeDefault.label}
              </span>
            </SelectWrapper>
          </div>

          <button
            className="button"
            onClick={handleCalculation}
            disabled={
              isLoading ||
              isCalculated ||
              selectedInputs.institutionType === undefined ||
              selectedInputs.seniority === undefined ||
              selectedInputs.weeklyHours === undefined
            }
          >
            Beregn
          </button>
        </div>
      </div>

      {isCalculated ? (
        <SalaryStatisticsOverview
          salaries={salaries}
          selectedInputs={selectedInputs}
        />
      ) : (
        errorMessage && <p>{errorMessage}</p>
      )}
    </>
  )
}

export default SalaryStatistics
